import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import * as moment from 'moment';
import {
  Grid,
  Col,
  RSelect,
  Input,
  InputContainer,
  Icon,
  Button,
} from '@common/components';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import {
  selectFilter,
  updateFilters,
  dateFilter,
  setDateFilter,
  applyFilters,
  updateTmpFilter,
  isDateFilterApply,
} from '@dashboard/slices/filters';
import { faCalendarDays, faClose } from '@fortawesome/free-solid-svg-icons';
import {
  MAX_DAYS,
  MIN_DAYS,
  DATE_FILTERS,
  DATE_FILTER_CUSTOM,
  DATE_FILTER_MIN_DATE,
} from '@dashboard/constants';
import { useTranslation } from 'react-i18next';
import i18n from '@common/config/i18n';

import viewClasess from './DateFilters.module.scss';

let lastDateFilters = null;
export default function DateFilters({ isLoading = false }) {
  const { t } = useTranslation('dashboard\\DateFilters');

  const dispatch = useAppDispatch();
  const currentDateFilterSel = useAppSelector(dateFilter);
  const currentFilters = useAppSelector(selectFilter);
  const isDateApply = useAppSelector(isDateFilterApply);
  const [fromDate, setFromDate] = useState(currentFilters.from || '');
  const [toDate, setToDate] = useState(currentFilters.to || '');
  const [loading, setLoading] = useState(isLoading);

  const [minDate] = useState(DATE_FILTER_MIN_DATE);
  const [maxDate] = useState(moment().subtract(1, 'day').format('YYYY-MM-DD'));

  const parseDateFilter = (f) => {
    const filter = DATE_FILTERS.find((cf) => cf.value === f.value);
    return {
      label: t(filter.label),
      value: filter.value,
      days: filter.days,
      months: filter.months,
    };
  };

  const parseDateFilters = (filters) => {
    return filters.map((f) => parseDateFilter(f));
  };

  const [dateFilters, setDateFilters] = useState(
    parseDateFilters(DATE_FILTERS),
  );

  const [currentDateFilter, setCurrentDateFilter] = useState(
    parseDateFilter(currentDateFilterSel),
  );

  const applyDateFilters = () => {
    Promise.all([
      dispatch(updateFilters({ from: fromDate, to: toDate })),
      dispatch(applyFilters()),
    ]);
  };

  useEffect(() => {
    const handleLanguageChanged = () => {
      setDateFilters(parseDateFilters(DATE_FILTERS));
      setCurrentDateFilter(
        parseDateFilter(lastDateFilters || currentDateFilterSel),
      );
    };

    i18n.on('languageChanged', handleLanguageChanged);
    i18n.on('loaded', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
      i18n.off('loaded', handleLanguageChanged);
    };
  }, []);

  useEffect(() => {
    lastDateFilters = { ...currentDateFilterSel };
    setCurrentDateFilter(parseDateFilter(currentDateFilterSel));
  }, [currentDateFilterSel]);

  useEffect(() => {
    if (!currentDateFilter) {
      setToDate('');
      setFromDate('');
      return;
    }

    const today = moment().subtract(1, 'day');
    if (currentDateFilter.value === DATE_FILTER_CUSTOM) {
      setFromDate(currentFilters.from);
      setToDate(currentFilters.to);
    } else {
      let to;
      let from;

      if (currentDateFilter.months) {
        const ago = moment()
          .subtract(currentDateFilter.months, 'months')
          .startOf('month');

        const minDateMoment = moment(minDate, 'YYYY-MM-DD');

        if (ago.isBefore(minDateMoment)) {
          from = minDate;
        } else {
          from = moment(ago).format('YYYY-MM-DD');
        }

        to = moment(today)
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD');
      } else {
        const ago = moment().subtract(currentDateFilter.days, 'days');
        from = moment(ago).format('YYYY-MM-DD');
        to = moment(today).format('YYYY-MM-DD');
      }

      if (toDate !== to || from !== fromDate) {
        Promise.all([
          dispatch(updateFilters({ from, to })),
          dispatch(applyFilters()),
        ]);
      }

      setToDate(to);
      setFromDate(from);
    }
  }, [currentDateFilter, minDate]);

  useEffect(() => {
    setLoading(isLoading === true);
  }, [isLoading]);

  useEffect(() => {
    dispatch(updateTmpFilter({ from: fromDate, to: toDate }));
  }, [fromDate, toDate]);

  const handleChangeDateFrom = (from) => {
    setFromDate(from);
    const diff = moment(toDate).diff(moment(from), 'days');
    if (!Number.isNaN(diff) && diff >= MAX_DAYS + 1) {
      setToDate(moment(from).add(MAX_DAYS, 'days').format('YYYY-MM-DD'));
    } else if (!Number.isNaN(diff) && diff < 0) {
      setToDate(moment(from).add(MIN_DAYS, 'days').format('YYYY-MM-DD'));
    }
  };

  const handleChangeDateTo = (to) => {
    setToDate(to);
    const diff = moment(to).diff(moment(fromDate), 'days');
    if (diff >= MAX_DAYS + 1) {
      setFromDate(moment(to).subtract(MAX_DAYS, 'days').format('YYYY-MM-DD'));
    }
  };

  const handleChangeDateFilter = (newDateFilter) => {
    dispatch(setDateFilter(newDateFilter));
  };

  const isNotDateCustom = () => currentDateFilter?.value !== DATE_FILTER_CUSTOM;

  return (
    <Grid>
      <Col xss={12} xs={12} sm={3}>
        <RSelect
          data={dateFilters}
          val={currentDateFilter}
          onChange={(e) => handleChangeDateFilter(e)}
          placeholder={t('inputPeriodPlaceholder')}
        />
      </Col>
      <Col xss={12} xs={12} sm={3}>
        <InputContainer
          suffix={
            <>
              {fromDate !== '' && (
                <div
                  className={viewClasess.close}
                  onClick={() => setFromDate('')}
                  aria-hidden="true"
                  role="button"
                  tabIndex="0"
                >
                  <Icon
                    icon={faClose}
                    color={isNotDateCustom() ? 'grey-400' : 'primary'}
                  />
                </div>
              )}

              <Icon
                icon={faCalendarDays}
                color={isNotDateCustom() ? 'grey-400' : 'primary'}
              />
            </>
          }
          placeholder={t('inputDateFromPlaceholder')}
          name="from"
          inputDisabled={isNotDateCustom()}
        >
          <Input
            className={viewClasess.iconLauncher}
            fullWidth
            type="date"
            placeholder={t('inputDateFromPlaceholder')}
            value={fromDate}
            name="from"
            disabled={isNotDateCustom()}
            min={minDate}
            max={toDate || maxDate}
            onMouseDown={(e) => {
              e.currentTarget.showPicker();
              e.preventDefault();
              return false;
            }}
            onChange={(event) => handleChangeDateFrom(event)}
          />
        </InputContainer>
      </Col>
      <Col xss={12} xs={12} sm={3}>
        <InputContainer
          suffix={
            <>
              {toDate !== '' && (
                <div
                  className={viewClasess.close}
                  onClick={() => setToDate('')}
                  aria-hidden="true"
                  role="button"
                  tabIndex="0"
                >
                  <Icon
                    icon={faClose}
                    color={isNotDateCustom() ? 'grey-400' : 'primary'}
                  />
                </div>
              )}

              <Icon
                icon={faCalendarDays}
                color={isNotDateCustom() ? 'grey-400' : 'primary'}
              />
            </>
          }
          placeholder={t('inputDateToPlaceholder')}
          name="to"
          inputDisabled={isNotDateCustom()}
        >
          <Input
            className={viewClasess.iconLauncher}
            fullWidth
            type="date"
            placeholder={t('inputDateToPlaceholder')}
            value={toDate}
            name="to"
            disabled={currentDateFilter?.value !== DATE_FILTER_CUSTOM}
            min={fromDate || minDate}
            max={maxDate}
            onMouseDown={(e) => {
              e.currentTarget.showPicker();
              e.preventDefault();
              return false;
            }}
            onChange={(event) => handleChangeDateTo(event)}
          />
        </InputContainer>
      </Col>
      {currentDateFilter?.value === DATE_FILTER_CUSTOM &&
        fromDate !== '' &&
        toDate !== '' && (
          <Col xss={12} xs={12} sm={3}>
            <div className={viewClasess.filterButton}>
              <Button
                color="accent"
                variant="flat"
                onClick={() => applyDateFilters()}
                disabled={loading || isDateApply}
                pointer
              >
                {t('btnSubmitLabel')}
              </Button>
            </div>
          </Col>
        )}
    </Grid>
  );
}

DateFilters.propTypes = {
  isLoading: PropTypes.bool,
};
